import type { IsoDate } from '../models/types';

/**
 * Tarih yardımcıları.
 *
 * ÖNEMLİ — neden `toISOString()` kullanmıyoruz:
 * `new Date().toISOString()` her zaman **UTC** saatine göre üretir.
 * Türkiye UTC+3 olduğu için, yerel saat gece 00:00–03:00 arasındayken
 * UTC hâlâ bir önceki gündedir. Yani gece 01:00'de bir alışkanlık
 * işaretlenseydi, kayıt DÜNE yazılırdı — kapanmış bir günü bozar ve
 * streak'i haksız yere koparabilirdi.
 *
 * Bu yüzden aşağıdaki fonksiyonlar her zaman cihazın **yerel**
 * takvim gününü kullanır.
 */

/** Tek haneli sayıları başına 0 koyarak iki haneye tamamlar (7 → "07"). */
function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** Bir `Date` nesnesini yerel takvim gününe göre `yyyy-mm-dd`'ye çevirir. */
export function toIsoDate(date: Date): IsoDate {
  // getMonth() 0'dan başlar (Ocak = 0), bu yüzden +1.
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Cihazın yerel saatine göre bugünün tarihi. */
export function todayIso(): IsoDate {
  return toIsoDate(new Date());
}

/**
 * `yyyy-mm-dd` metnini bir `Date` nesnesine çevirir.
 *
 * Saat olarak öğlen 12:00 seçiliyor — gece yarısı değil. Sebep: yaz
 * saati uygulamasına geçilen günlerde saatler 1 ileri/geri alınır ve
 * gece yarısına sabitlenmiş bir tarih, gün eklerken yanlışlıkla bir
 * önceki/sonraki güne kayabilir. Öğlen, bu 1 saatlik kaymaya karşı
 * güvenli bir tampon bırakır.
 */
function parseIsoDate(date: IsoDate): Date {
  const [year, month, day] = date.split('-').map(Number);
  // noUncheckedIndexedAccess açık olduğu için parçaların gerçekten
  // geldiğini doğruluyoruz.
  if (year === undefined || month === undefined || day === undefined) {
    throw new Error(`Geçersiz tarih biçimi: "${date}" (yyyy-mm-dd bekleniyor)`);
  }
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

/** Verilen tarihe gün ekler (negatif sayı ile gün çıkarır). */
export function addDays(date: IsoDate, days: number): IsoDate {
  const parsed = parseIsoDate(date);
  parsed.setDate(parsed.getDate() + days);
  return toIsoDate(parsed);
}

/**
 * İki tarih arasındaki tam gün farkı (`to - from`).
 * Örn. daysBetween('2026-09-01', '2026-09-04') === 3.
 */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const diff = parseIsoDate(to).getTime() - parseIsoDate(from).getTime();
  // Yaz saati geçişlerinde fark tam 24 saatin katı olmayabilir; öğlene
  // sabitlediğimiz için sapma en fazla 1 saattir, yuvarlama bunu siler.
  return Math.round(diff / MS_PER_DAY);
}

/**
 * `yyyy-mm-dd` biçimindeki tarihler metin olarak da doğru sıralanır
 * (yıl-ay-gün sırası bunu garanti eder), bu yüzden karşılaştırma için
 * `Date` nesnesi üretmeye gerek yok.
 */
export function isOnOrBefore(date: IsoDate, other: IsoDate): boolean {
  return date <= other;
}
